import { useEffect, useState } from "react";
import { useAction } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useTranslation } from "@/packages/utils";
import { motion } from "framer-motion";
import {
  Upload,
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileUp,
} from "lucide-react";
import { Button, Input } from "@/components/ui/primitives";

interface BackupMetadata {
  version: number;
  createdAt: string;
  scope: "full" | "workspace";
  workspaceId: string | null;
  tableCount: number;
  recordCount: number;
  fileCount: number;
}

export function BackupImportPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(["admin"]);
  const importDb = useAction(api.admin_backup.importDatabase);
  const generateUploadUrl = useAction(api.admin_backup.generateUploadUrl);
  const listWorkspacesAction = useAction(api.admin_backup.listWorkspaces);

  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<BackupMetadata | null>(null);
  const [workspaces, setWorkspaces] = useState<
    Array<{ _id: Id<"workspaces">; name: string; slug: string }>
  >([]);
  const [targetWorkspaceValue, setTargetWorkspaceValue] = useState("");
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [isImporting, setIsImporting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmValue, setConfirmValue] = useState("");
  const [result, setResult] = useState<{
    tablesRestored: number;
    recordsRestored: number;
    filesRestored: number;
    skipped: string[];
  } | null>(null);

  const selectedTargetWorkspace =
    workspaces.find((workspace) => workspace._id === targetWorkspaceValue) ??
    null;
  const requiresTargetWorkspace = metadata?.scope === "workspace";

  useEffect(() => {
    if (metadata?.scope !== "workspace") {
      setTargetWorkspaceValue("");
      return;
    }

    let isCancelled = false;
    setIsLoadingWorkspaces(true);

    listWorkspacesAction({})
      .then((result) => {
        if (isCancelled) return;
        setWorkspaces(result);
      })
      .catch((err) => {
        if (isCancelled) return;
        setStatus(
          `${t("actions.importRestore.failed")}: ${err instanceof Error ? err.message : String(err)}`,
        );
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingWorkspaces(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [listWorkspacesAction, metadata?.scope, t]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setBackupFile(file);
    setResult(null);
    setShowConfirm(false);
    setConfirmValue("");
    setTargetWorkspaceValue("");
    setStatus(t("actions.importRestore.readingFile"));

    try {
      const text = await file.text();
      const backup = JSON.parse(text);

      if (backup.version !== 1) {
        setStatus(
          t("actions.importRestore.unsupportedVersion", {
            version: backup.version,
          }),
        );
        setMetadata(null);
        return;
      }

      const tableCount = Object.keys(backup.tables ?? {}).length;
      const recordCount = Object.values(backup.tables ?? {}).reduce(
        (sum: number, arr: unknown) =>
          sum + (Array.isArray(arr) ? arr.length : 0),
        0,
      );
      const fileCount = Object.keys(backup.files ?? {}).length;

      setMetadata({
        version: backup.version,
        createdAt: backup.createdAt,
        scope: backup.scope,
        workspaceId: backup.workspaceId,
        tableCount,
        recordCount,
        fileCount,
      });
      setStatus("");
    } catch (err) {
      setStatus(
        `${t("actions.importRestore.invalidFile")}: ${err instanceof Error ? err.message : String(err)}`,
      );
      setMetadata(null);
    }
  }

  async function handleRestore() {
    if (!backupFile || !metadata) return;
    if (metadata.scope === "workspace" && !selectedTargetWorkspace) {
      setStatus(t("actions.importRestore.selectTargetWorkspace"));
      return;
    }

    setShowConfirm(false);
    setConfirmValue("");
    setIsImporting(true);
    setStatus(t("actions.importRestore.readingFile"));

    try {
      const text = await backupFile.text();
      const backup = JSON.parse(text);

      const storageIdMap: Record<string, string> = {};
      const fileEntries = Object.entries(backup.files ?? {});

      if (fileEntries.length > 0) {
        let uploaded = 0;
        for (const [oldStorageId, base64Data] of fileEntries) {
          setStatus(
            t("actions.importRestore.uploadingFiles", {
              current: uploaded + 1,
              total: fileEntries.length,
            }),
          );

          try {
            const binaryString = atob(base64Data as string);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes]);

            const uploadUrl = await generateUploadUrl({});

            const uploadResponse = await fetch(uploadUrl, {
              method: "POST",
              headers: { "Content-Type": "application/octet-stream" },
              body: blob,
            });

            if (!uploadResponse.ok) {
              throw new Error(`Upload failed: ${uploadResponse.status}`);
            }

            const { storageId } = await uploadResponse.json();
            storageIdMap[oldStorageId] = storageId;
          } catch (err) {
            console.error(`Failed to upload file ${oldStorageId}:`, err);
          }
          uploaded++;
        }
      }

      setStatus(t("actions.importRestore.restoringRecords"));

      const importResult = await importDb({
        scope: backup.scope,
        targetWorkspaceId:
          backup.scope === "workspace"
            ? selectedTargetWorkspace?._id
            : undefined,
        tables: backup.tables,
        storageIdMap,
      });

      setResult(importResult);
      setStatus(
        backup.scope === "full"
          ? t("actions.importRestore.completeRedirecting")
          : "",
      );

      if (backup.scope === "full") {
        // Best-effort: clear JS-accessible cookies. HttpOnly cookies cannot be
        // cleared from JavaScript — they will be cleared server-side on the next
        // request or by the auth provider. The redirect below still runs even if
        // cookie clearing is incomplete.
        try {
          document.cookie.split(";").forEach((c) => {
            document.cookie = c
              .replace(/^ +/, "")
              .replace(
                /=.*/,
                "=;expires=" + new Date(0).toUTCString() + ";path=/",
              );
          });
          localStorage.clear();
          sessionStorage.clear();
        } catch {
          // Ignore errors from cookie/storage clearing — redirect still proceeds
        }

        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      }
    } catch (err) {
      setStatus(
        `${t("actions.importRestore.failed")}: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setIsImporting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 backdrop-blur-[2px] pt-[8vh] pb-12"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg mx-4"
      >
        <div className="rounded-2xl border bg-card shadow-2xl shadow-black/10">
          {/* Danger stripe */}
          <div className="h-1 rounded-t-2xl bg-gradient-to-r from-amber-400 via-orange-400 to-red-400" />

          {/* Header */}
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 ring-1 ring-amber-500/20">
                  <Upload className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h2
                    className="text-lg font-bold text-foreground"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {t("actions.importRestore.label")}
                  </h2>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    {t("actions.importRestore.description")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Danger banner */}
          <div className="mx-6 mb-4 flex items-center gap-2.5 rounded-lg bg-amber-50 border border-amber-200/60 px-3.5 py-2.5 text-xs text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span className="font-medium">
              {t("actions.importRestore.dangerWarning")}
            </span>
          </div>

          {/* File picker */}
          <div className="px-6 space-y-4">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("actions.importRestore.backupFile")}
              </p>
              <label className="flex h-24 w-full cursor-pointer items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border bg-muted/20 text-sm text-muted-foreground transition-colors hover:border-amber-300 hover:bg-amber-50/30">
                <FileUp className="h-5 w-5" />
                <span>
                  {backupFile
                    ? backupFile.name
                    : t("actions.importRestore.selectFile")}
                </span>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isImporting}
                />
              </label>
            </div>

            {metadata?.scope === "workspace" && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("actions.importRestore.targetWorkspace")}
                </p>
                <select
                  className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:ring-offset-2"
                  value={targetWorkspaceValue}
                  onChange={(e) => setTargetWorkspaceValue(e.target.value)}
                  disabled={isImporting || isLoadingWorkspaces}
                >
                  <option value="">
                    {isLoadingWorkspaces
                      ? t("actions.importRestore.loadingTargetWorkspaces")
                      : t(
                          "actions.importRestore.selectTargetWorkspacePlaceholder",
                        )}
                  </option>
                  {workspaces.map((workspace) => (
                    <option key={workspace._id} value={workspace._id}>
                      {workspace.name} ({workspace.slug})
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  {t("actions.importRestore.targetWorkspaceHelp")}
                </p>
              </div>
            )}

            {/* Metadata preview */}
            {metadata && (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("actions.importRestore.meta.date")}
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {metadata.createdAt}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("actions.importRestore.meta.scope")}
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {metadata.scope}
                  </span>
                </div>
                {metadata.workspaceId && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("actions.importRestore.meta.sourceWorkspace")}
                    </span>
                    <span className="font-mono font-medium text-foreground truncate ml-4 max-w-[200px]">
                      {metadata.workspaceId}
                    </span>
                  </div>
                )}
                {selectedTargetWorkspace && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("actions.importRestore.meta.targetWorkspace")}
                    </span>
                    <span className="font-mono font-medium text-foreground truncate ml-4 max-w-[200px]">
                      {selectedTargetWorkspace.name} (
                      {selectedTargetWorkspace.slug})
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("actions.importRestore.meta.tables")}
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {metadata.tableCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("actions.importRestore.meta.records")}
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {metadata.recordCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("actions.importRestore.meta.files")}
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {metadata.fileCount}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Confirmation */}
          {showConfirm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.2 }}
              className="mx-6 mt-4"
            >
              <div className="rounded-lg border-2 border-dashed border-red-300/60 bg-red-50/50 p-4 space-y-2.5">
                <p className="text-xs font-bold text-red-700 uppercase tracking-wider">
                  {t("actions.confirmTitle")}
                </p>
                <p className="text-xs text-red-600/80">
                  {t("actions.importRestore.confirmDescription")}
                </p>
                <Input
                  placeholder={t("actions.confirmPlaceholder")}
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value)}
                  autoFocus
                  className="h-10 border-red-200 bg-white focus:ring-red-400/30 font-mono text-sm"
                />
              </div>
            </motion.div>
          )}

          {/* Action bar */}
          <div className="px-6 pt-5 pb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("actions.cancel")}
            </button>

            {metadata && !isImporting && !showConfirm && (
              <Button
                onClick={() => setShowConfirm(true)}
                variant="danger"
                className="min-w-[140px] h-10"
                disabled={requiresTargetWorkspace && !selectedTargetWorkspace}
              >
                {t("actions.importRestore.restore")}
              </Button>
            )}

            {showConfirm && (
              <Button
                onClick={handleRestore}
                disabled={confirmValue !== "import-restore"}
                variant="danger"
                className="min-w-[140px] h-10"
              >
                {t("actions.importRestore.confirmRestore")}
              </Button>
            )}

            {isImporting && (
              <Button disabled variant="danger" className="min-w-[140px] h-10">
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t("actions.executing")}
                </span>
              </Button>
            )}
          </div>

          {/* Status */}
          {isImporting && status && (
            <div className="border-t border-amber-200 bg-amber-50 px-6 py-4 rounded-b-2xl">
              <p className="text-xs text-amber-700 font-medium">{status}</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-green-200 bg-green-50 px-6 py-4 rounded-b-2xl"
            >
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <div className="space-y-1 text-xs">
                  <p className="text-sm font-semibold text-green-800">
                    {t("actions.importRestore.restoreComplete")}
                  </p>
                  <p className="text-green-700/70">
                    {result.tablesRestored} tables, {result.recordsRestored}{" "}
                    records, {result.filesRestored} files
                  </p>
                  {selectedTargetWorkspace && (
                    <p className="text-green-700/70">
                      {t("actions.importRestore.workspaceOverrideComplete", {
                        workspace: selectedTargetWorkspace.name,
                      })}
                    </p>
                  )}
                  {result.skipped.length > 0 && (
                    <div className="mt-2">
                      <p className="font-bold text-amber-700">Skipped:</p>
                      <ul className="list-inside list-disc text-amber-600">
                        {result.skipped.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Error status */}
          {!isImporting && !result && status && (
            <div className="border-t border-red-200 bg-red-50 px-6 py-4 rounded-b-2xl">
              <div className="flex items-start gap-2.5">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <p className="text-xs text-red-700">{status}</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
